import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Banknote, ArrowRight, Phone } from 'lucide-react';

const money = (n) => `£${Number(n || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const stepLabel = (step) => {
  if (step >= 2) return { text: 'All chasers sent · call them', cls: 'bg-rose-100 text-rose-700 border-rose-200', phone: true };
  if (step === 1) return { text: '1 chaser sent', cls: 'bg-amber-100 text-amber-700 border-amber-200', phone: false };
  return { text: 'No chaser yet', cls: 'bg-slate-100 text-slate-600 border-slate-200', phone: false };
};

/**
 * Dashboard widget: signed customers whose deposit is still unpaid, with how
 * many automated chaser emails have gone out. Mirrors the DNO reminder widget.
 */
export const DepositsOutstandingWidget = () => {
  const [data, setData] = useState({ items: [], total: 0, chased_out_count: 0 });
  const navigate = useNavigate();

  const load = () => {
    api.get('/deposits/outstanding')
      .then((r) => setData(r.data || { items: [], total: 0, chased_out_count: 0 }))
      .catch(() => {});
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, []);

  if (!data.items?.length) return null;

  return (
    <Card className="border-emerald-300 bg-emerald-50/60" data-testid="deposits-outstanding-widget">
      <CardContent className="p-5">
        <div className="flex items-start gap-3 mb-3">
          <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center flex-shrink-0">
            <Banknote className="w-5 h-5 text-emerald-600" />
          </div>
          <div>
            <p className="font-semibold text-slate-900">
              {data.total} deposit{data.total === 1 ? '' : 's'} outstanding
            </p>
            <p className="text-sm text-slate-600 mt-0.5">
              Signed customers who haven't paid their deposit yet.
              {data.chased_out_count > 0 && (
                <span className="text-rose-600 font-medium"> {data.chased_out_count} need a call — email chasers exhausted.</span>
              )}
            </p>
          </div>
        </div>

        <ul className="space-y-2">
          {data.items.slice(0, 5).map((it) => {
            const s = stepLabel(it.drip_step);
            return (
              <li
                key={it.customer_id}
                className="flex items-center justify-between gap-3 bg-white border border-emerald-100 rounded-lg px-3 py-2"
                data-testid={`deposit-item-${it.customer_id}`}
              >
                <div className="min-w-0">
                  <p className="text-sm font-medium text-slate-800 truncate">
                    {it.customer_name || 'Customer'}
                    <span className="text-slate-500 font-normal"> · {money(it.amount)}</span>
                  </p>
                  <p className="text-xs text-slate-500 truncate">
                    {it.address}{it.postcode ? `, ${it.postcode}` : ''}
                  </p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <Badge className={`text-[10px] ${s.cls}`} data-testid={`deposit-step-${it.customer_id}`}>
                    {s.phone && <Phone className="w-3 h-3 mr-1" />}{s.text}
                  </Badge>
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-8 border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                    onClick={() => navigate(`/customers/${it.customer_id}`)}
                    data-testid={`deposit-open-btn-${it.customer_id}`}
                  >
                    Open <ArrowRight className="w-3.5 h-3.5 ml-1" />
                  </Button>
                </div>
              </li>
            );
          })}
        </ul>
      </CardContent>
    </Card>
  );
};
